import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { ContexteSession, Role } from "@hopitech/contracts";
import { connexionEmail, deconnexionEmail, inscriptionEmail, moi, ouvrirSessionDev, sante, stopImpersonate } from "../lib/api.ts";
import { EDITIONS, type Edition } from "./config.ts";

/**
 * État applicatif du SPA. Deux modes : « better-auth » (connexion réelle) ou « stub » (session
 * de développement). Le mode vient du serveur (/api/sante) ; le SPA ne le devine pas.
 * En mode réel, le rôle et l'édition viennent des RATTACHEMENTS du serveur (/api/moi),
 * jamais d'un choix client (ADR-0009 : les droits se vérifient sur le rattachement).
 */

type Mode = "stub" | "better-auth";

interface AppState {
  mode: Mode;
  edition: Edition;
  role: Role;
  compte: string;
  connecte: boolean;
  usurpation: { actif: boolean; cible: string };
  connecter: (p: { compte: string; role: Role }) => Promise<void>;
  connecterEmail: (email: string, motDePasse: string) => Promise<void>;
  inscrire: (email: string, motDePasse: string, nom: string) => Promise<void>;
  changerRole: (role: Role) => Promise<void>;
  changerEdition: (id: number) => Promise<void>;
  rafraichir: () => Promise<void>;
  deconnecter: () => void;
  demarrerUsurpation: (cible: string) => void;
  arreterUsurpation: () => void | Promise<void>;
}

const Ctx = createContext<AppState | null>(null);
const EDITION_DEFAUT = EDITIONS[0] as Edition;

export function AppProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [mode, setMode] = useState<Mode>("stub");
  const [edition, setEdition] = useState<Edition>(EDITION_DEFAUT);
  const [role, setRole] = useState<Role>("exposant");
  const [compte, setCompte] = useState<string>("");
  const [connecte, setConnecte] = useState<boolean>(false);
  const [usurpation, setUsurpation] = useState<{ actif: boolean; cible: string }>({ actif: false, cible: "" });

  useEffect(() => {
    sante().then((s) => setMode(s.authentification)).catch(() => setMode("stub"));
  }, []);

  const appliquerContexte = useCallback((ctx: ContexteSession): void => {
    setCompte(ctx.compte);
    setUsurpation(ctx.usurpation ? { actif: true, cible: ctx.compte } : { actif: false, cible: "" });
    const premier = ctx.rattachements[0];
    if (premier) {
      setRole(premier.role);
      const ed = EDITIONS.find((e) => e.id === premier.edition);
      if (ed) setEdition(ed);
    }
    setConnecte(true);
  }, []);

  useEffect(() => {
    if (mode === "better-auth") void moi().then(appliquerContexte).catch(() => undefined);
  }, [mode, appliquerContexte]);

  const connecter = useCallback<AppState["connecter"]>(async ({ compte, role }) => {
    await ouvrirSessionDev({ compte, edition: edition.id, role });
    setCompte(compte);
    setRole(role);
    setConnecte(true);
  }, [edition.id]);

  const connecterEmail = useCallback<AppState["connecterEmail"]>(async (email, motDePasse) => {
    await connexionEmail(email, motDePasse);
    appliquerContexte(await moi());
  }, [appliquerContexte]);

  const inscrire = useCallback<AppState["inscrire"]>(async (email, motDePasse, nom) => {
    await inscriptionEmail(email, motDePasse, nom);
    appliquerContexte(await moi());
  }, [appliquerContexte]);

  const changerRole = useCallback<AppState["changerRole"]>(async (nouveauRole) => {
    if (mode === "stub") await ouvrirSessionDev({ compte: compte || "demo", edition: edition.id, role: nouveauRole });
    setRole(nouveauRole); // en mode réel, simple bascule de vue ; les droits restent ceux du rattachement
  }, [mode, compte, edition.id]);

  const changerEdition = useCallback<AppState["changerEdition"]>(async (id) => {
    const cible = EDITIONS.find((e) => e.id === id) ?? EDITION_DEFAUT;
    setEdition(cible);
    if (mode === "stub" && connecte) await ouvrirSessionDev({ compte: compte || "demo", edition: cible.id, role });
  }, [mode, connecte, compte, role]);

  const rafraichir = useCallback(async (): Promise<void> => {
    appliquerContexte(await moi());
  }, [appliquerContexte]);

  const arreterUsurpation = useCallback(async (): Promise<void> => {
    if (mode === "better-auth") {
      await stopImpersonate().catch(() => undefined);
      await rafraichir();
    } else {
      setUsurpation({ actif: false, cible: "" });
    }
  }, [mode, rafraichir]);

  const deconnecter = useCallback((): void => {
    if (mode === "better-auth") void deconnexionEmail().catch(() => undefined);
    setConnecte(false);
    setCompte("");
    setUsurpation({ actif: false, cible: "" });
  }, [mode]);

  const valeur = useMemo<AppState>(() => ({
    mode, edition, role, compte, connecte, usurpation,
    connecter, connecterEmail, inscrire, changerRole, changerEdition, rafraichir, deconnecter,
    demarrerUsurpation: (cible) => setUsurpation({ actif: true, cible }),
    arreterUsurpation,
  }), [mode, edition, role, compte, connecte, usurpation, connecter, connecterEmail, inscrire, changerRole, changerEdition, rafraichir, deconnecter, arreterUsurpation]);

  return <Ctx.Provider value={valeur}>{children}</Ctx.Provider>;
}

export function useApp(): AppState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useApp hors d'un AppProvider");
  return ctx;
}
